import { Grid } from "@mui/material";
import useDisclosure from "@/hooks/use-disclosure";
import Flex from "@/components/Flex";
import Text from "@/components/Text";
import CustomTabs from "@/components/app/CustomTabs";
import StyledButtonV2 from "@/components/app/StyledButtonV2";
import { getSelectedRowCount } from "@/utils/data-grid";
import {
  CHART_COLORS,
  CHART_TYPOGRAPHY,
} from "@/components/app/provider/patient/chart";
import {
  GROUP_DETAIL_LABEL,
  GROUP_DETAIL_TAB,
  GROUP_DETAIL_TABS,
  MANAGE_PATIENTS_LABEL,
} from "../group-visit-constant";
import {
  GroupDetail,
  GroupDetailTab,
  GroupPatient,
  PatientRowAction,
} from "../group-visit-types";
import { useGroupNotificationSettings } from "../hooks/use-group-notification-settings";
import { useGroupPatients } from "../hooks/use-group-patients";
import { useGroupTherapists } from "../hooks/use-group-therapists";
import AddPatientDrawer from "./AddPatientDrawer";
import CareTeamCard from "./CareTeamCard";
import ProviderAssignmentModal from "./ProviderAssignmentModal";
import ManageTherapistsTab from "./ManageTherapistsTab";
import ManagePatientsTab from "./ManagePatientsTab";
import RemovePatientSessionsModal from "./RemovePatientSessionsModal";
import NotificationsRemindersTab from "./NotificationsRemindersTab";
import GroupInformationCard from "./GroupInformationCard";
import NextSessionCard from "./NextSessionCard";

export type GroupDetailsProps = {
  group: GroupDetail;
  onEditGroupDetails: () => void;
  onPatientRowAction?: (
    action: PatientRowAction,
    patient: GroupPatient
  ) => void;
  /** Tab on screen — the route owns it, so a tab is directly linkable. */
  activeTab: GroupDetailTab;
  onTabChange: (tab: GroupDetailTab) => void;
  canEdit?: boolean;
};

const TABS_SX = {
  "& .MuiTabs-flexContainer": { justifyContent: "flex-start" },
  "&.MuiTabs-root": { justifyContent: "flex-start" },
};

/**
 * @author Rajkumar Rathod
 */
const GroupDetails = ({
  group,
  onEditGroupDetails,
  onPatientRowAction,
  activeTab,
  onTabChange,
  canEdit = true,
}: GroupDetailsProps) => {
  const addPatientDisclosure = useDisclosure();

  const roster = useGroupPatients(group.uuid);
  const therapists = useGroupTherapists(group.uuid);
  const notifications = useGroupNotificationSettings(group.uuid);
  const assignmentDisclosure = useDisclosure();
  const removeSelectedDisclosure = useDisclosure();

  const isPatientsTab = activeTab === GROUP_DETAIL_TAB.PATIENTS;

  const handleRemoveSelectedConfirm = async (
    cancelUpcomingSessions: boolean
  ) => {
    try {
      await roster.removeSelected(cancelUpcomingSessions);
      removeSelectedDisclosure.close();
    } catch {
      // The hook already surfaced the error; keep the dialog open to retry.
    }
  };

  return (
    <Flex flexDirection="column" gap={2} height="100%">
      <Flex
        alignItems="center"
        justifyContent="space-between"
        gap={2}
        flexWrap="wrap"
      >
        <CustomTabs
          value={activeTab}
          tabsData={GROUP_DETAIL_TABS}
          onChange={(value) => onTabChange(value as GroupDetailTab)}
          sx={TABS_SX}
        />

        {canEdit && activeTab === GROUP_DETAIL_TAB.DETAILS && (
          <StyledButtonV2
            label={GROUP_DETAIL_LABEL.editGroupDetails}
            variant="primary"
            shouldEditIcon
            onClick={onEditGroupDetails}
          />
        )}

        {canEdit && isPatientsTab && (
          <Flex gap={1}>
            <StyledButtonV2
              label={MANAGE_PATIENTS_LABEL.removeSelected}
              variant="outlined"
              disabled={!getSelectedRowCount(roster.selection)}
              onClick={() => removeSelectedDisclosure.open()}
            />
            <StyledButtonV2
              label={MANAGE_PATIENTS_LABEL.addPatients}
              variant="primary"
              onClick={addPatientDisclosure.open}
            />
          </Flex>
        )}
      </Flex>

      {activeTab === GROUP_DETAIL_TAB.THERAPISTS && (
        <ManageTherapistsTab
          therapists={therapists}
          canEdit={canEdit}
          onAddCoTherapist={assignmentDisclosure.open}
        />
      )}

      {activeTab === GROUP_DETAIL_TAB.NOTIFICATIONS && (
        <NotificationsRemindersTab
          settings={notifications.settings}
          onChange={notifications.updateSetting}
          disabled={!canEdit || notifications.isLoading}
        />
      )}

      {isPatientsTab && !!onPatientRowAction && (
        <ManagePatientsTab
          roster={roster}
          canEdit={canEdit}
          onRowAction={onPatientRowAction}
        />
      )}

      <RemovePatientSessionsModal
        open={removeSelectedDisclosure.opened}
        onClose={removeSelectedDisclosure.close}
        isBulk
        onConfirm={handleRemoveSelectedConfirm}
        isSubmitting={roster.isRemoving}
      />

      {assignmentDisclosure.opened && (
        <ProviderAssignmentModal
          open={assignmentDisclosure.opened}
          onClose={assignmentDisclosure.close}
          values={therapists.assignment}
          onSubmit={therapists.saveAssignment}
        />
      )}

      {addPatientDisclosure.opened && (
        <AddPatientDrawer
          opened={addPatientDisclosure.opened}
          onClose={addPatientDisclosure.close}
          options={roster.patientOptions}
          onSearch={roster.setOptionSearchKey}
          onSubmit={roster.addPatients}
        />
      )}

      {activeTab === GROUP_DETAIL_TAB.DETAILS ? (
        <Grid
          container
          spacing={2}
          sx={{
            alignItems: "flex-start",
          }}
        >
          <Grid
            size={{
              xs: 12,
              lg: 8,
            }}
          >
            <Flex flexDirection="column" gap={2}>
              <GroupInformationCard group={group} />
              <CareTeamCard group={group} />
            </Flex>
          </Grid>

          <Grid
            size={{
              xs: 12,
              lg: 4,
            }}
          >
            <NextSessionCard group={group} />
          </Grid>
        </Grid>
      ) : (
        activeTab !== GROUP_DETAIL_TAB.THERAPISTS &&
        !(isPatientsTab && !!onPatientRowAction) &&
        activeTab !== GROUP_DETAIL_TAB.NOTIFICATIONS && (
          <Text {...CHART_TYPOGRAPHY.body5} c={CHART_COLORS.textMutedGrey}>
            {GROUP_DETAIL_LABEL.tabPlaceholder}
          </Text>
        )
      )}
    </Flex>
  );
};

export default GroupDetails;
